import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ToolSidebar from '../../utils/ToolSideBar';
import {
  createTeamNote,
  fetchTeam,
  fetchTeamNotes,
  renameTeam,
  requestEmploymentReview,
  requestMemberRemoval,
  submitTeamRating,
} from '../../../services/teamApi';
import '../../../css/superuser/StaffDirectoryPage.css';
import '../../../css/staff/InternalWorkspace.css';

const removalReasons = [
  ['performance', 'Performance concerns'],
  ['availability', 'Attendance/availability'],
  ['team_fit', 'Team fit'],
  ['reassignment', 'Reassignment recommended'],
  ['project_needs', 'Project needs changed'],
  ['conduct', 'Conduct concern'],
  ['other', 'Other'],
];

const employmentReasons = [
  ['performance', 'Repeated performance problems'],
  ['misconduct', 'Misconduct'],
  ['policy', 'Policy violation'],
  ['missed_commitments', 'Repeated missed commitments'],
  ['availability', 'Attendance/availability'],
  ['other', 'Other'],
];

function currentRoleInfo() {
  const role = localStorage.getItem('portal_role') || 'client';
  const isSuperuser = localStorage.getItem('is_superuser') === 'true';
  return { role, isSuperuser, canRate: isSuperuser || role === 'hr' };
}

function HeaderMetric({ label, value, children }) {
  return (
    <div className="team-detail-metric">
      <span>{label}</span>
      <strong>{children || value || 'Not assigned'}</strong>
    </div>
  );
}

function FormMessage({ message, error }) {
  if (!message && !error) return null;
  return <p className={error ? 'team-form-message is-error' : 'team-form-message'}>{error || message}</p>;
}

function RatingForm({ teamId, onSubmitted }) {
  const [rating, setRating] = useState('5.0');
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await submitTeamRating(teamId, { rating, notes });
      setNotes('');
      setMessage('Rating submitted.');
      setError('');
      onSubmitted?.();
    } catch (err) {
      setError(err.message || 'Unable to submit rating.');
      setMessage('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="team-action-form" onSubmit={handleSubmit}>
      <label>
        <span>Team rating</span>
        <select value={rating} onChange={(event) => setRating(event.target.value)}>
          {['5.0', '4.5', '4.0', '3.5', '3.0', '2.5', '2.0', '1.5', '1.0'].map((item) => <option key={item} value={item}>{item} / 5</option>)}
        </select>
      </label>
      <label>
        <span>Notes</span>
        <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional private rating note" />
      </label>
      <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Submit Rating'}</button>
      <FormMessage message={message} error={error} />
    </form>
  );
}

function RenameForm({ team, onRenamed }) {
  const [name, setName] = useState(team.name || '');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { setName(team.name || ''); }, [team.name]);

  async function handleSubmit(event) {
    event.preventDefault();
    try {
      await renameTeam(team.id, name);
      setMessage('Team renamed.');
      setError('');
      onRenamed?.();
    } catch (err) {
      setError(err.message || 'Unable to rename team.');
      setMessage('');
    }
  }

  return (
    <form className="team-action-form compact" onSubmit={handleSubmit}>
      <label>
        <span>Team name</span>
        <input value={name} onChange={(event) => setName(event.target.value)} />
      </label>
      <button type="submit">Rename Team</button>
      <FormMessage message={message} error={error} />
    </form>
  );
}

function NotesPanel({ team, reloadTeam }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTeamNotes(team.id);
      setNotes(data.results || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load internal notes.');
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  async function handleSubmit(event) {
    event.preventDefault();
    try {
      await createTeamNote(team.id, body);
      setBody('');
      await loadNotes();
      reloadTeam?.();
    } catch (err) {
      setError(err.message || 'Unable to add note.');
    }
  }

  return (
    <section className="staff-card team-section">
      <div className="staff-card-header"><h2>Internal Team Notes</h2></div>
      <div className="team-section-body">
        {loading ? <p className="staff-empty inline">Loading notes...</p> : error ? <p className="team-form-message is-error">{error}</p> : notes.length === 0 ? <p className="staff-empty inline">No internal notes yet.</p> : (
          <div className="team-note-list">
            {notes.map((note) => <article key={note.id} className="team-note"><p>{note.body}</p><small>{note.author?.name || 'SCD'} · {new Date(note.created_at).toLocaleString()}</small></article>)}
          </div>
        )}
        <form className="team-action-form" onSubmit={handleSubmit}>
          <label>
            <span>Add internal note</span>
            <textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder="Add PM context for this team" required />
          </label>
          <button type="submit">Add Note</button>
        </form>
      </div>
    </section>
  );
}

function PersonnelRequestForm({ team, type }) {
  const groups = team.member_groups || [];
  const members = groups.flatMap((group) => (group.members || []).map((member) => ({ ...member, groupLabel: group.label })));
  const [memberId, setMemberId] = useState(members[0]?.id || '');
  const [employeeId, setEmployeeId] = useState(members[0]?.user?.id || '');
  const [reason, setReason] = useState(type === 'removal' ? removalReasons[0][0] : employmentReasons[0][0]);
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (type === 'removal') {
      const member = members.find((item) => String(item.id) === String(memberId));
      if (member) setEmployeeId(member.user.id);
    }
  }, [memberId, members, type]);

  async function handleSubmit(event) {
    event.preventDefault();
    try {
      const payload = type === 'removal'
        ? { membership_id: Number(memberId), reason_category: reason, details }
        : { employee_id: Number(employeeId), reason_category: reason, details };
      const result = type === 'removal'
        ? await requestMemberRemoval(team.id, payload)
        : await requestEmploymentReview(team.id, payload);
      setStatus(result.status_label || 'Pending Review');
      setDetails('');
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to submit personnel request.');
      setStatus('');
    }
  }

  const reasons = type === 'removal' ? removalReasons : employmentReasons;
  const title = type === 'removal' ? 'Request Removal' : 'Request Employment Review';

  return (
    <form className="team-action-form" onSubmit={handleSubmit}>
      <h3>{title}</h3>
      <label>
        <span>Team member</span>
        <select value={type === 'removal' ? memberId : employeeId} onChange={(event) => type === 'removal' ? setMemberId(event.target.value) : setEmployeeId(event.target.value)} required>
          {members.map((member) => <option key={`${type}-${member.id}`} value={type === 'removal' ? member.id : member.user.id}>{member.user.name} · {member.role_label}</option>)}
        </select>
      </label>
      <label>
        <span>Reason</span>
        <select value={reason} onChange={(event) => setReason(event.target.value)}>{reasons.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
      </label>
      <label>
        <span>Detailed explanation</span>
        <textarea value={details} onChange={(event) => setDetails(event.target.value)} required placeholder="Provide enough context for HR/superuser review." />
      </label>
      <button type="submit">Submit Request</button>
      <FormMessage message={status ? `Request submitted: ${status}` : ''} error={error} />
    </form>
  );
}

function MemberGroups({ groups }) {
  return (
    <section className="staff-card team-section">
      <div className="staff-card-header"><h2>Team Members</h2></div>
      <div className="team-member-groups">
        {(groups || []).map((group) => (
          <article className="team-member-group" key={group.discipline}>
            <h3>{group.label}</h3>
            {group.members?.length ? group.members.map((member) => (
              <div className="team-member-row" key={member.id}>
                <strong>{member.user.name}</strong>
                <span>{member.role_label}{member.developer_discipline ? ` · ${member.developer_discipline}` : ''}</span>
              </div>
            )) : <p>{group.empty_message || `No ${group.label} currently assigned.`}</p>}
          </article>
        ))}
      </div>
    </section>
  );
}

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const roleInfo = useMemo(() => currentRoleInfo(), []);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTeam(teamId);
      setTeam(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load team.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  if (loading) {
    return <div className="staff-shell"><ToolSidebar active="team" /><main className="staff-page internal-page team-page"><div className="team-detail-skeleton" /></main></div>;
  }

  if (error || !team) {
    return <div className="staff-shell"><ToolSidebar active="team" /><main className="staff-page internal-page team-page"><section className="staff-card"><p className="staff-empty inline team-error">{error || 'Team not found.'}</p></section></main></div>;
  }

  const project = team.current_project;
  const health = team.project_health;
  const canManage = Boolean(team.can_manage);
  const canRequest = Boolean(team.can_request_personnel_review);

  return (
    <div className="staff-shell">
      <ToolSidebar active="team" />
      <main className="staff-page internal-page team-page">
        <header className="staff-header internal-hero team-detail-header">
          <Link to="/team" className="staff-strong-link">Back to Teams</Link>
          <span>DELIVERY TEAM</span>
          <h1>{team.name}</h1>
          <div className="team-detail-metrics">
            <HeaderMetric label="Team Type" value={team.team_type_label || 'Delivery'} />
            <HeaderMetric label="Project Manager" value={team.project_manager?.name || 'Unassigned'} />
            <HeaderMetric label="Current Project" value={project?.name || 'No active project'} />
            <HeaderMetric label="Health">{health ? <span className={`health-pill health-${health.key}`}>{health.label}</span> : 'No active project'}</HeaderMetric>
            <HeaderMetric label="Rating" value={team.rating?.display || 'Not rated'} />
          </div>
        </header>

        <MemberGroups groups={team.member_groups} />

        <section className="team-detail-grid">
          {canManage && <section className="staff-card team-section"><div className="staff-card-header"><h2>Management Actions</h2></div><div className="team-section-body"><RenameForm team={team} onRenamed={loadTeam} />{canRequest && <><PersonnelRequestForm team={team} type="removal" /><PersonnelRequestForm team={team} type="employment" /></>}</div></section>}
          {roleInfo.canRate && <section className="staff-card team-section"><div className="staff-card-header"><h2>Team Rating</h2></div><div className="team-section-body"><RatingForm teamId={team.id} onSubmitted={loadTeam} /></div></section>}
        </section>

        {team.can_create_internal_note && <NotesPanel team={team} reloadTeam={loadTeam} />}
      </main>
    </div>
  );
}